<template>
  <b-container fluid>
    <b-row>
      <b-col
        lg="4"
      >
        <b-form-group>
          <label>
            {{ $t('fields.asset.asset-info.general-data.disconnector.type') }}
          </label>
          <s-input-label-edit
            v-model="value.type"
            :editable="editing"
            :formatted-value="typeFormattedValue"
          >
            <s-enum-select
              v-model="value.type"
              :enum-definition="voltageTypeEnum"
              translation-path="enums.asset-info.polarity"
            />
          </s-input-label-edit>
        </b-form-group>
      </b-col>
      <b-col
        lg="8"
      >
        <b-form-group>
          <label>
            {{ $t('fields.asset.asset-info.general-data.disconnector.maneuver-type') }}
          </label>
          <s-input-label-edit
            v-model="value.maneuverType"
            :formatted-value="maneuverTypeFormattedValue"
            :editable="editing"
          >
            <s-enum-select
              v-model="value.maneuverType"
              :enum-definition="maneuverTypeEnum"
              translation-path="enums.asset-info.maneuver-type"
            />
          </s-input-label-edit>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col
        lg="4"
      >
        <b-form-group>
          <label>
            {{ $t('fields.asset.asset-info.general-data.disconnector.nominal-tension') }}
          </label>
          <s-input-label-edit
            v-model="value.nominalTension"
            :formatted-value="formatNumberValue(value.nominalTension, 2)"
            :editable="editing"
          >
            <s-number-input
              class="text-right"
              v-model="value.nominalTension"
              :validation="$v.value.nominalTension"
              :invalid="$v.value.nominalTension.$invalid"
              :numeric-options="getNumericOptionsPrecision('nominalTension')"
            />
            <b-form-invalid-feedback>
              {{ getRangeValueMessage('nominalTension') }}
            </b-form-invalid-feedback>
          </s-input-label-edit>
        </b-form-group>
      </b-col>
      <b-col
        lg="4"
      >
        <b-form-group>
          <label>
            {{ $t('fields.asset.asset-info.general-data.disconnector.nominal-chain') }}
          </label>
          <s-input-label-edit
            v-model="value.nominalChain"
            :formatted-value="formatNumberValue(value.nominalChain, 2)"
            :editable="editing"
          >
            <s-number-input
              class="text-right"
              v-model="value.nominalChain"
              :validation="$v.value.nominalChain"
              :invalid="$v.value.nominalChain.$invalid"
              :numeric-options="getNumericOptionsPrecision('nominalChain')"
            />
            <b-form-invalid-feedback>
              {{ getRangeValueMessage('nominalChain') }}
            </b-form-invalid-feedback>
          </s-input-label-edit>
        </b-form-group>
      </b-col>
      <b-col
        lg="4"
      >
        <b-form-group>
          <label>
            {{ $t('fields.asset.asset-info.general-data.disconnector.nominal-frequency') }}
          </label>
          <s-input-label-edit
            v-model="nominalFrequencyProxy"
            :formatted-value="formatNumberValue(value.nominalFrequency)"
            :editable="editing"
          >
            <b-form-input
              class="text-right"
              v-model="nominalFrequencyProxy"
              v-mask="'###'"
              :disabled="saving"
              :state="$bsStateValidator($v.value.nominalFrequency.$invalid)"
            />
            <b-form-invalid-feedback>
              {{ getRangeValueMessage('nominalFrequency') }}
            </b-form-invalid-feedback>
          </s-input-label-edit>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col
        xl="8"
      >
        <b-container fluid
          class="border rounded mb-1 h-100"
        >
          <b-row align-v="end" class="h-100 pt-3">
            <b-col cols="12" align-self="start">
              <label>
                {{ $t('fields.asset.asset-info.general-data'
                  + '.disconnector.supportable-nominal-tension') }}
              </label>
            </b-col>
            <b-col
              lg="6"
            >
              <b-form-group>
                <label>
                  {{ $t('fields.asset.asset-info.general-data'
                  + '.disconnector.applied-voltage-1-min') }}
                </label>
                <s-input-label-edit
                  v-model="value.appliedTension1Min"
                  :formatted-value="formatNumberValue(value.appliedTension1Min)"
                  :editable="editing"
                >
                  <s-number-input
                    class="text-right"
                    v-model="appliedTension1MinProxy"
                    :validation="$v.value.appliedTension1Min"
                    :invalid="$v.value.appliedTension1Min.$invalid"
                    :numeric-options="getNumericOptionsPrecision('appliedTension1Min')"
                  />
                  <b-form-invalid-feedback>
                    {{ getRangeValueMessage('appliedTension1Min') }}
                  </b-form-invalid-feedback>
                </s-input-label-edit>
              </b-form-group>
            </b-col>
            <b-col
              lg="6"
            >
              <b-form-group>
                <label>
                  {{ $t('fields.asset.asset-info.general-data'
                  + '.disconnector.atmospheric-impulse') }}
                </label>
                <s-input-label-edit
                  v-model="atmosphericImpulseProxy"
                  :formatted-value="formatNumberValue(value.atmosphericImpulse)"
                  :editable="editing"
                >
                  <s-number-input
                    class="text-right"
                    v-model="atmosphericImpulseProxy"
                    :validation="$v.value.atmosphericImpulse"
                    :invalid="$v.value.atmosphericImpulse.$invalid"
                    :numeric-options="getNumericOptionsPrecision('atmosphericImpulse')"
                  />
                  <b-form-invalid-feedback>
                    {{ getRangeValueMessage('atmosphericImpulse') }}
                  </b-form-invalid-feedback>
                </s-input-label-edit>
              </b-form-group>
            </b-col>
          </b-row>
        </b-container>
      </b-col>
        <b-col
          xl="4"
        >
        <b-form-group>
          <label>
            {{ $t('fields.asset.asset-info.general-data'
              + '.disconnector.supportable-short-chain-duration') }}
          </label>
          <s-input-label-edit
            v-model="value.supportableShortChainDuration"
            :editable="editing"
            :input-options="shortTermBearableCurrentFieldOptions"
          >
          </s-input-label-edit>
        </b-form-group>
        <b-form-group>
          <label>
            {{ $t('fields.asset.asset-info.general-data'
              + '.disconnector.installation-place') }}
          </label>
          <s-input-label-edit
            v-model="value.installationPlace"
            :formatted-value="installationPlaceFormattedValue"
            :editable="editing"
          >
            <s-enum-select
              v-model="value.installationPlace"
              translation-path="enums.asset-info.installation-place"
              :enum-definition="installationPlaceEnum"
              :disabled="saving"
            />
          </s-input-label-edit>
        </b-form-group>
      </b-col>
    </b-row>
    <b-row class="mt-3">
      <b-col>
        <b-form-group>
          <label>
            {{ $t('fields.asset.asset-info.general-data.disconnector.maintenance-history') }}
          </label>
          <s-input-label-edit
            v-model.trim="value.maintenanceHistory"
            maxlength="2000"
            input-type="textarea"
            :editable="editing"
            :input-options="inputOptions.maintenanceHistory"
          />
        </b-form-group>
      </b-col>
    </b-row>
    <b-row>
      <b-col>
        <b-form-group>
          <label>{{ $t('fields.general.observations')}}</label>
          <s-input-label-edit
            v-model.trim="value.observation"
            maxlength="2000"
            input-type="textarea"
            :editable="editing"
            :input-options="inputOptions.observation"
          />
        </b-form-group>
      </b-col>
    </b-row>
  </b-container>
</template>
<script>
import { between } from 'vuelidate/lib/validators';
import { InstallationPlace, DisconnectorCircuitBreakerType, ManeuverType } from 'sigma-mappings';

const shortTermBearableCurrentFieldInvalidCharsMatcher = /[^A-Za-zÀ-Úà-ú\d,]/g;

const rangeValidations = {
  appliedTension1Min: {
    min: 1,
    max: 100000,
    precision: 0,
  },
  atmosphericImpulse: {
    min: 1,
    max: 100000,
    precision: 0,
  },
  nominalFrequency: {
    min: 1,
    max: 100,
    precision: 0,
  },
  nominalTension: {
    min: 0.01,
    max: 10000,
    precision: 2,
  },
  nominalChain: {
    min: 0.01,
    max: 10000,
    precision: 2,
  },
};

const validationsToMerge = {
  value: {},
};

Object.keys(rangeValidations).forEach((fieldName) => {
  validationsToMerge.value[fieldName] = {
    between: between(rangeValidations[fieldName].min, rangeValidations[fieldName].max),
  };
});

function stringToNumberComputedGetSetBuilder({ get, set }) {
  return {
    get,
    set(value) {
      let valueToSet = value;
      if (value === '') {
        valueToSet = null;
      }

      if (typeof valueToSet === 'string') {
        valueToSet = Number.parseInt(valueToSet, 10);
      }

      set.call(this, valueToSet);
    },
  };
}

export default {
  computed: {
    appliedTension1MinProxy: stringToNumberComputedGetSetBuilder({
      get() {
        return this.value.appliedTension1Min;
      },
      set(value) {
        this.$set(this.value, 'appliedTension1Min', value);
      },
    }),
    atmosphericImpulseProxy: stringToNumberComputedGetSetBuilder({
      get() {
        return this.value.atmosphericImpulse;
      },
      set(value) {
        this.$set(this.value, 'atmosphericImpulse', value);
      },
    }),
    nominalFrequencyProxy: stringToNumberComputedGetSetBuilder({
      get() {
        return this.value.nominalFrequency;
      },
      set(value) {
        this.$set(this.value, 'nominalFrequency', value);
      },
    }),
    shortTermBearableCurrentFieldOptions() {
      return {
        disabled: this.saving,
        formatter: newValue => newValue
          .replace(shortTermBearableCurrentFieldInvalidCharsMatcher, ''),
        maxLength: 25,
        class: 'text-right',
      };
    },
    typeFormattedValue() {
      if (this.value.type == null) {
        return null;
      }

      return this.$t(`enums.asset-info.polarity.${this.value.type}`);
    },
    maneuverTypeFormattedValue() {
      if (this.value.maneuverType == null) {
        return null;
      }

      return this.$t(`enums.asset-info.maneuver-type.${this.value.maneuverType}`);
    },
    installationPlaceFormattedValue() {
      if (this.value.installationPlace == null) {
        return null;
      }

      return this.$t(`enums.asset-info.installation-place.${this.value.installationPlace}`);
    },
    inputOptions() {
      return {
        nominalTension: {
          disabled: this.saving,
          maxLength: 9,
          state: this.$bsStateValidator(this.$v.value.nominalTension.$invalid),
        },
        nominalChain: {
          disabled: this.saving,
          maxLength: 9,
          state: this.$bsStateValidator(this.$v.value.nominalChain.$invalid),
        },
        appliedTension1MinProxy: {
          disabled: this.saving,
          maxLength: 7,
          state: this.$bsStateValidator(this.$v.value.appliedTension1Min.$invalid),
        },
        atmosphericImpulse: {
          disabled: this.saving,
          maxLength: 7,
          state: this.$bsStateValidator(this.$v.value.atmosphericImpulse.$invalid),
        },
        maintenanceHistory: {
          disabled: this.saving,
          maxLength: 2000,
        },
        observation: {
          disabled: this.saving,
          maxLength: 2000,
        },
      };
    },
    invalidData: {
      cache: false,
      get() {
        return this.$v.$invalid;
      },
    },
  },
  data: () => ({
    voltageTypeEnum: DisconnectorCircuitBreakerType,
    maneuverTypeEnum: ManeuverType,
    installationPlaceEnum: InstallationPlace,
  }),
  methods: {
    getNumericOptionsPrecision(field) {
      return {
        decimalPlaces: rangeValidations[field].precision,
      };
    },
    formatNumberValue(value, decimais = 0) {
      if (typeof value !== 'number') {
        return value;
      }
      return this.$n(value, {
        minimumFractionDigits: decimais,
        maximumFractionDigits: decimais,
      });
    },
    getRangeValueMessage(fieldName) {
      const options = {
        minimumFractionDigits: rangeValidations[fieldName].precision,
        maximumFractionDigits: rangeValidations[fieldName].precision,
      };
      return this.$t('messages.MA086.message', {
        minValue: this.$n(rangeValidations[fieldName].min, options),
        maxValue: this.$n(rangeValidations[fieldName].max, options),
      });
    },
    emitValidationState() {
      this.$emit('validate', !this.$v.$invalid);
    },
  },
  props: {
    value: Object,
    editing: Boolean,
    saving: Boolean,
  },
  validations: {
    // Caso adicionar mais validações, fazer um merge profundo.
    ...validationsToMerge,
  },
  watch: {
    invalidData() {
      this.emitValidationState();
    },
  },
};
</script>
